import { List } from '@refinedev/antd';
import {
  Table,
  Tag,
  Space,
  message,
  InputNumber,
  Button,
  Select,
  Card,
  Row,
  Col,
  Typography,
  Popconfirm,
  Tooltip,
} from 'antd';
import { ThunderboltOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useState, useEffect, useMemo } from 'react';
import { apiClient } from '../../lib/api-client';
import { t } from '../../i18n';

const { Text } = Typography;

interface Region {
  id: number;
  nameUz: string;
  nameRu: string;
}

interface RoutePricing {
  id: number;
  fromRegionId: number;
  toRegionId: number;
  tripType: string;
  surgeMultiplier: number;
  isActive: boolean;
  fromRegion?: Region;
  toRegion?: Region;
  fromCity?: { id: number; nameUz: string } | null;
  toCity?: { id: number; nameUz: string } | null;
}

const tripTypeLabels: Record<string, string> = {
  INTERREGION_TAXI: 'Viloyatlar aro taksi',
  SHAHARLAR_ARO: 'Viloyat ichida taksi',
  YOL_YOLAKAY: 'Viloyatlar aro savdolashish',
};

const tripTypeColors: Record<string, string> = {
  INTERREGION_TAXI: 'blue',
  SHAHARLAR_ARO: 'cyan',
  YOL_YOLAKAY: 'green',
};

// Quick-pick presets shown as chips for one-tap surge setting.
const SURGE_PRESETS = [1.0, 1.1, 1.3, 1.5, 2.0];

const getSurgeColor = (m: number) => {
  if (m >= 2) return 'red';
  if (m >= 1.5) return 'orange';
  if (m > 1) return 'gold';
  return 'default';
};

const surgeLabel = (m: number) =>
  m > 1 ? `+${Math.round((m - 1) * 100)}%` : 'Oddiy';

export const RouteSurgeManager = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [routes, setRoutes] = useState<RoutePricing[]>([]);
  const [loading, setLoading] = useState(false);
  const [fromRegionId, setFromRegionId] = useState<number | undefined>();
  const [bulkSurge, setBulkSurge] = useState<number>(1.3);
  const [savingId, setSavingId] = useState<number | null>(null);
  // Local per-row edits before save.
  const [edits, setEdits] = useState<Record<number, number>>({});

  useEffect(() => {
    fetchRegions();
    fetchRoutes();
  }, []);

  const fetchRegions = async () => {
    try {
      const res = await apiClient.get('/admin/regions');
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRegions(data);
    } catch {
      message.error('Viloyatlarni yuklashda xatolik');
    }
  };

  const fetchRoutes = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/pricing/routes');
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRoutes(data);
      setEdits({});
    } catch {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const visibleRoutes = useMemo(
    () =>
      fromRegionId
        ? routes.filter((r) => r.fromRegionId === fromRegionId)
        : routes,
    [routes, fromRegionId],
  );

  const regionName = (id?: number) =>
    regions.find((r) => r.id === id)?.nameUz || `ID: ${id}`;

  const saveOne = async (record: RoutePricing) => {
    const value = edits[record.id] ?? Number(record.surgeMultiplier) ?? 1;
    setSavingId(record.id);
    try {
      await apiClient.put(`/admin/pricing/routes/${record.id}`, {
        surgeMultiplier: value,
      });
      message.success(
        `${regionName(record.fromRegionId)} → ${regionName(record.toRegionId)}: x${value.toFixed(2)}`,
      );
      setRoutes((prev) =>
        prev.map((r) =>
          r.id === record.id ? { ...r, surgeMultiplier: value } : r,
        ),
      );
      setEdits((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
    } catch {
      message.error(t('common.error'));
    } finally {
      setSavingId(null);
    }
  };

  const applyBulk = async () => {
    if (!fromRegionId) {
      message.warning('Avval viloyatni tanlang');
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post('/admin/pricing/routes/surge/bulk', {
        fromRegionId,
        surgeMultiplier: bulkSurge,
      });
      const updated = res.data?.updated ?? 0;
      message.success(
        `${regionName(fromRegionId)} dan chiquvchi ${updated} ta yo'nalishga x${bulkSurge.toFixed(2)} qo'llandi`,
      );
      await fetchRoutes();
    } catch {
      message.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Yo\'nalish',
      key: 'route',
      render: (_: unknown, r: RoutePricing) => (
        <Space size={4}>
          <Text strong>
            {r.fromRegion?.nameUz || regionName(r.fromRegionId)}
            {r.fromCity ? ` (${r.fromCity.nameUz})` : ''}
          </Text>
          <Text type="secondary">→</Text>
          <Text>
            {r.toRegion?.nameUz || regionName(r.toRegionId)}
            {r.toCity ? ` (${r.toCity.nameUz})` : ''}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Xizmat turi',
      key: 'tripType',
      render: (_: unknown, r: RoutePricing) => (
        <Tag color={tripTypeColors[r.tripType] || 'default'}>
          {tripTypeLabels[r.tripType] || r.tripType}
        </Tag>
      ),
    },
    {
      title: (
        <Space>
          Surge koeffitsienti
          <Tooltip title="Narxni ko'paytiruvchi koeffitsient. 1.0 = oddiy narx, 1.3 = +30%">
            <InfoCircleOutlined style={{ color: '#7F8C8D' }} />
          </Tooltip>
        </Space>
      ),
      key: 'surge',
      render: (_: unknown, r: RoutePricing) => {
        const current = edits[r.id] ?? Number(r.surgeMultiplier) ?? 1;
        const dirty = edits[r.id] !== undefined && edits[r.id] !== Number(r.surgeMultiplier);
        return (
          <Space wrap>
            <InputNumber
              value={current}
              min={1}
              max={5}
              step={0.1}
              style={{ width: 90 }}
              onChange={(val) =>
                setEdits((prev) => ({ ...prev, [r.id]: Number(val) || 1 }))
              }
            />
            {SURGE_PRESETS.map((p) => (
              <Tag
                key={p}
                color={current === p ? getSurgeColor(p) : 'default'}
                style={{ cursor: 'pointer', margin: 0 }}
                onClick={() =>
                  setEdits((prev) => ({ ...prev, [r.id]: p }))
                }
              >
                x{p.toFixed(1)}
              </Tag>
            ))}
            <Tag color={getSurgeColor(current)} icon={current > 1 ? <ThunderboltOutlined /> : undefined}>
              {surgeLabel(current)}
            </Tag>
            <Button
              type="primary"
              size="small"
              disabled={!dirty}
              loading={savingId === r.id}
              onClick={() => saveOne(r)}
            >
              Saqlash
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <List
      title={
        <Space>
          <ThunderboltOutlined /> Surge boshqaruvi
        </Space>
      }
      headerButtons={<Button onClick={fetchRoutes}>Yangilash</Button>}
    >
      <Card style={{ marginBottom: 16, background: '#F5F7FA' }}>
        <p style={{ marginTop: 0, color: '#7F8C8D' }}>
          <InfoCircleOutlined style={{ marginRight: 8 }} />
          Bu yerda yo'nalishlarga surge koeffitsientini qo'llang. Masalan,
          Toshkentni tanlab x1.3 bossangiz — Toshkentdan chiquvchi barcha
          yo'nalishlar narxi 30% ga oshadi.
        </p>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={8}>
            <Select
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Chiqish viloyatini tanlang"
              style={{ width: '100%' }}
              value={fromRegionId}
              onChange={(v) => setFromRegionId(v)}
            >
              {regions.map((rg) => (
                <Select.Option key={rg.id} value={rg.id}>
                  {rg.nameUz}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} md={5}>
            <InputNumber
              value={bulkSurge}
              min={1}
              max={5}
              step={0.1}
              style={{ width: '100%' }}
              addonBefore="x"
              onChange={(v) => setBulkSurge(Number(v) || 1)}
            />
          </Col>
          <Col xs={12} md={4}>
            <Tag color={getSurgeColor(bulkSurge)} style={{ fontSize: 14, padding: '4px 10px' }}>
              {surgeLabel(bulkSurge)}
            </Tag>
          </Col>
          <Col xs={24} md={7}>
            <Popconfirm
              title={
                fromRegionId
                  ? `${regionName(fromRegionId)} dan chiquvchi barcha yo'nalishlarga x${bulkSurge.toFixed(2)} qo'llansinmi?`
                  : 'Avval viloyatni tanlang'
              }
              onConfirm={applyBulk}
              okText="Ha"
              cancelText="Yo'q"
              disabled={!fromRegionId}
            >
              <Button
                type="primary"
                icon={<ThunderboltOutlined />}
                disabled={!fromRegionId}
                block
              >
                Hammasiga qo'llash
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table
        dataSource={visibleRoutes}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: true }}
      />
    </List>
  );
};
